import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FiMenu, FiCheck, FiX, FiUser, FiClock, FiSearch } from 'react-icons/fi';
import AdminSidebar from './AdminSidebar';
import { getAllRequests, approveRequest, declineRequest } from '../../Redux/slices/ministrySlice';

const MinistryRequests = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterMinistry, setFilterMinistry] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const dispatch = useDispatch();
  const { requests } = useSelector(state => state.ministry);

  useEffect(() => {
    dispatch(getAllRequests());
  }, [dispatch]);

  const ministryOptions = [
    { value: 'worship-team', label: 'Worship Team' },
    { value: 'media-ministry', label: 'Media Ministry' },
    { value: 'creative-arts', label: 'Creative Arts' },
    { value: 'children-ministry', label: "Children's Ministry" },
    { value: 'youth-ministry', label: 'Youth Ministry' },
    { value: 'adult-ministry', label: 'Adult Ministry' },
    { value: 'community-outreach', label: 'Community Outreach' },
    { value: 'missions', label: 'Missions Ministry' },
    { value: 'pastoral-care', label: 'Pastoral Care' },
    { value: 'discipleship', label: 'Discipleship Training' },
    { value: 'small-groups', label: 'Small Groups' },
    { value: 'ushering', label: 'Ushering & Hospitality' },
    { value: 'prayer-team', label: 'Prayer Team' },
  ];

  const handleApprove = async (id) => {
    if (window.confirm('Are you sure you want to approve this request?')) {
      await dispatch(approveRequest(id));
      dispatch(getAllRequests());
      setSelectedRequest(null);
    }
  };

  const handleDecline = async (id, reason) => {
    const declineReason = reason || prompt('Please provide a reason for declining:');
    if (declineReason) {
      await dispatch(declineRequest({ id, reason: declineReason }));
      dispatch(getAllRequests());
      setSelectedRequest(null);
    }
  };

  const filteredRequests = requests.filter(request => {
    const matchesSearch = 
      request.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || request.status === filterStatus;
    const matchesMinistry = filterMinistry === 'all' || request.ministry === filterMinistry;
    return matchesSearch && matchesStatus && matchesMinistry;
  });

  const pendingCount = requests.filter(r => r.status === 'pending').length;
  const approvedCount = requests.filter(r => r.status === 'approved').length;
  const declinedCount = requests.filter(r => r.status === 'declined').length;

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'declined': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <AdminSidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} currentPage="ministry-requests" />

      <div className="lg:ml-64">
        <header className="bg-white shadow-sm">
          <div className="flex items-center justify-between p-4">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden">
              <FiMenu size={24} />
            </button>
            <h2 className="text-2xl font-bold text-gray-800">Ministry Join Requests</h2>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-800">Admin User</p>
                <p className="text-xs text-gray-500">admin@houseofpraise.com</p>
              </div>
              <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold">
                A
              </div>
            </div>
          </div>
        </header>

        <main className="p-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm mb-1">Pending Requests</p>
                  <p className="text-3xl font-bold text-gray-800">{pendingCount}</p>
                </div>
                <div className="bg-yellow-100 p-4 rounded-lg">
                  <FiClock className="text-yellow-600" size={24} />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm mb-1">Approved</p>
                  <p className="text-3xl font-bold text-gray-800">{approvedCount}</p>
                </div>
                <div className="bg-green-100 p-4 rounded-lg">
                  <FiCheck className="text-green-600" size={24} />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm mb-1">Declined</p>
                  <p className="text-3xl font-bold text-gray-800">{declinedCount}</p>
                </div>
                <div className="bg-red-100 p-4 rounded-lg">
                  <FiX className="text-red-600" size={24} />
                </div>
              </div>
            </div>
          </div>

          {/* Filters and Search */}
          <div className="bg-white rounded-xl shadow-md p-6 mb-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="declined">Declined</option>
              </select>

              <select
                value={filterMinistry}
                onChange={(e) => setFilterMinistry(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Ministries</option>
                {ministryOptions.map((ministry) => (
                  <option key={ministry.value} value={ministry.value}>
                    {ministry.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Requests Table */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left py-4 px-6 text-gray-600 font-semibold">Name</th>
                    <th className="text-left py-4 px-6 text-gray-600 font-semibold">Ministry</th>
                    <th className="text-left py-4 px-6 text-gray-600 font-semibold">Contact</th>
                    <th className="text-left py-4 px-6 text-gray-600 font-semibold">Submitted</th>
                    <th className="text-left py-4 px-6 text-gray-600 font-semibold">Status</th>
                    <th className="text-left py-4 px-6 text-gray-600 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredRequests.map((request) => (
                    <tr key={request._id} className="hover:bg-gray-50">
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <FiUser className="text-blue-600" />
                          </div>
                          <div>
                            <p className="text-gray-800 font-semibold">{request.firstName} {request.lastName}</p>
                            <p className="text-gray-500 text-sm">{request.city}, {request.state}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-gray-800">{request.ministryLabel}</td>
                      <td className="py-4 px-6">
                        <div className="text-sm">
                          <p className="text-gray-800">{request.email}</p>
                          <p className="text-gray-500">{request.phone}</p>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-gray-600">{new Date(request.createdAt).toLocaleDateString()}</td>
                      <td className="py-4 px-6">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                          {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex gap-2">
                          <button
                            onClick={() => setSelectedRequest(request)}
                            className="text-blue-600 hover:text-blue-700 font-medium text-sm"
                          >
                            View
                          </button>
                          {request.status === 'pending' && (
                            <>
                              <button
                                onClick={() => handleApprove(request.id)}
                                className="text-green-600 hover:text-green-700 font-medium text-sm"
                              >
                                Approve
                              </button>
                              <button
                                onClick={() => handleDecline(request.id)}
                                className="text-red-600 hover:text-red-700 font-medium text-sm"
                              >
                                Decline
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </main>
      </div>

      {/* Request Details Modal */}
      {selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
              <h3 className="text-2xl font-bold text-gray-800">Request Details</h3>
              <button
                onClick={() => setSelectedRequest(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <FiX size={24} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Personal Information */}
              <div>
                <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <FiUser className="text-blue-600" />
                  Personal Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                  <div>
                    <p className="text-sm text-gray-500">Name</p>
                    <p className="text-gray-800 font-semibold">{selectedRequest.firstName} {selectedRequest.lastName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="text-gray-800">{selectedRequest.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Phone</p>
                    <p className="text-gray-800">{selectedRequest.phone}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Location</p>
                    <p className="text-gray-800">{selectedRequest.city}, {selectedRequest.state}</p>
                  </div>
                  <div className="md:col-span-2">
                    <p className="text-sm text-gray-500">Address</p>
                    <p className="text-gray-800">{selectedRequest.address}</p>
                  </div>
                </div>
              </div>

              {/* Ministry Information */}
              <div>
                <h4 className="text-lg font-bold text-gray-800 mb-4">Ministry Information</h4>
                <div className="space-y-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-500 mb-1">Selected Ministry</p>
                    <p className="text-gray-800 font-semibold">{selectedRequest.ministryLabel}</p>
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-500 mb-2">Availability</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedRequest.availability.map((time, index) => (
                        <span key={index} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                          {time}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-500 mb-1">Why Join?</p>
                    <p className="text-gray-800">{selectedRequest.whyJoin}</p>
                  </div>

                  {selectedRequest.previousMinistry && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-500 mb-1">Previous Ministry Experience</p>
                      <p className="text-gray-800">{selectedRequest.previousMinistry}</p>
                    </div>
                  )}

                  {selectedRequest.skills && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-500 mb-1">Skills & Talents</p>
                      <p className="text-gray-800">{selectedRequest.skills}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Status Information */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-gray-500">Status</p>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedRequest.status)}`}>
                    {selectedRequest.status.charAt(0).toUpperCase() + selectedRequest.status.slice(1)}
                  </span>
                </div>
                <p className="text-sm text-gray-600">Submitted: {new Date(selectedRequest.createdAt).toLocaleDateString()}</p>
                {selectedRequest.reviewedDate && (
                  <p className="text-sm text-gray-600">Reviewed: {new Date(selectedRequest.reviewedDate).toLocaleDateString()}</p>
                )}
                {selectedRequest.declineReason && (
                  <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded">
                    <p className="text-sm text-red-800"><strong>Decline Reason:</strong> {selectedRequest.declineReason}</p>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              {selectedRequest.status === 'pending' && (
                <div className="flex gap-4 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => handleApprove(selectedRequest._id)}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition flex items-center justify-center gap-2"
                  >
                    <FiCheck size={20} />
                    Approve Request
                  </button>
                  <button
                    onClick={() => handleDecline(selectedRequest._id, selectedRequest.declineReason)}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-lg transition flex items-center justify-center gap-2"
                  >
                    <FiX size={20} />
                    Decline Request
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default MinistryRequests;
